import React, { useState, useEffect } from 'react';
import { Box, IconButton, Tooltip, Stack, Typography, Modal } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import axiosInstance from '../../api/axiosInstance';

interface CertImageViewerProps {
    /**
     * Cert row id — used to build a backend-streamed file URL. We don't take
     * the raw `sourceFileUrl` because that can be `file://` (dev) or `gs://`
     * (prod), neither of which a browser can fetch directly.
     */
    certId: string | null;
    /** Storage URL (used only to infer PDF-vs-image by extension). */
    sourceFileUrl?: string | null;
    /** Optional explicit MIME for routing PDF vs image. */
    mimeType?: string | null;
}

const isPdfHint = (url: string | null | undefined, mime?: string | null) =>
    (mime ?? '').toLowerCase() === 'application/pdf' || /\.pdf(\?|$)/i.test(url ?? '');

const buildFileUrl = (certId: string): string => {
    const base = axiosInstance.defaults.baseURL?.replace(/\/$/, '') ?? '';
    return `${base}/worker-certifications/${certId}/file/canonical`;
};

/**
 * Side-by-side viewer for cert review. Zoom + rotate + open-in-new for images,
 * iframe embed for PDFs.
 */
const CertImageViewer: React.FC<CertImageViewerProps> = ({ certId, sourceFileUrl, mimeType }) => {
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);

    useEffect(() => {
        setZoom(1);
        setRotation(0);
        setLightboxOpen(false);
    }, [certId]);

    if (!certId || !sourceFileUrl) {
        return (
            <Box
                sx={{
                    height: '100%',
                    minHeight: 400,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.default',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    No source file attached.
                </Typography>
            </Box>
        );
    }

    const url = buildFileUrl(certId);
    const pdf = isPdfHint(sourceFileUrl, mimeType);

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Stack
                direction="row"
                spacing={1}
                sx={{
                    p: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                }}
            >
                {!pdf && (
                    <>
                        <Tooltip title="Zoom out">
                            <IconButton size="small" onClick={() => setZoom(z => Math.max(0.5, z - 0.25))}>
                                <ZoomOutIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'center' }}>
                            {Math.round(zoom * 100)}%
                        </Typography>
                        <Tooltip title="Zoom in">
                            <IconButton size="small" onClick={() => setZoom(z => Math.min(3, z + 0.25))}>
                                <ZoomInIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Rotate 90°">
                            <IconButton size="small" onClick={() => setRotation(r => (r + 90) % 360)}>
                                <RotateRightIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
                <Tooltip title="Open in new tab">
                    <IconButton size="small" onClick={() => window.open(url, '_blank')}>
                        <OpenInNewIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>
            <Box
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    bgcolor: 'background.default',
                    display: 'flex',
                    alignItems: pdf ? 'stretch' : 'center',
                    justifyContent: pdf ? 'stretch' : 'center',
                    p: pdf ? 0 : 2,
                }}
            >
                {pdf ? (
                    <iframe
                        src={url}
                        style={{ width: '100%', height: '100%', minHeight: 500, border: 0 }}
                        title="Certificate PDF"
                    />
                ) : (
                    <img
                        src={url}
                        alt="Certificate"
                        onClick={() => setLightboxOpen(true)}
                        title="Click to enlarge"
                        style={{
                            transform: `scale(${zoom}) rotate(${rotation}deg)`,
                            transformOrigin: 'center',
                            maxWidth: '100%',
                            transition: 'transform 150ms ease',
                            cursor: 'zoom-in',
                        }}
                    />
                )}
            </Box>

            {!pdf && (
                <Modal
                    open={lightboxOpen}
                    onClose={() => setLightboxOpen(false)}
                    aria-label="Certificate preview"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(0,0,0,0.9)',
                    }}
                >
                    <Box
                        onClick={() => setLightboxOpen(false)}
                        sx={{
                            outline: 'none',
                            position: 'relative',
                            width: '100vw',
                            height: '100vh',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'zoom-out',
                        }}
                    >
                        <IconButton
                            onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
                            sx={{
                                position: 'absolute',
                                top: 16,
                                right: 16,
                                color: 'common.white',
                                bgcolor: 'rgba(0,0,0,0.4)',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                            }}
                            aria-label="Close preview"
                        >
                            <CloseIcon />
                        </IconButton>
                        <img
                            src={url}
                            alt="Certificate preview"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                maxWidth: '95vw',
                                maxHeight: '95vh',
                                objectFit: 'contain',
                                transform: `rotate(${rotation}deg)`,
                                cursor: 'default',
                            }}
                        />
                    </Box>
                </Modal>
            )}
        </Box>
    );
};

export default CertImageViewer;
